import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, Gauge, Loader2, Play, SkipForward } from "lucide-react";

import { GAME_OPTIONS } from "../data/scenarios/config";
import { MissionExperience } from "../components/game/MissionExperience";
import { LineBreakText } from "../components/game/LineBreakText";

const GameMission = () => {
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [playingIntroVideos, setPlayingIntroVideos] = useState<
    Record<string, boolean>
  >({});
  const [introVideoReady, setIntroVideoReady] = useState<
    Record<string, boolean>
  >({});
  const [playbackRates, setPlaybackRates] = useState<Record<string, number>>(
    {}
  );

  const handleSelectGame = (gameId: string) => {
    // 모든 비디오 일시정지
    Object.values(introVideoRefs.current).forEach((video) => {
      if (video) {
        try {
          video.pause();
        } catch (error) {
          // 무시
        }
      }
    });

    setSelectedGameId(gameId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const introVideoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  const handleIntroVideoOverlayClick = (
    event: React.MouseEvent,
    gameId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const video = introVideoRefs.current[gameId];
    if (!video) {
      return;
    }
    const isAtEnd =
      video.duration &&
      !isNaN(video.duration) &&
      video.currentTime >= video.duration - 0.1;

    if (isAtEnd) {
      video.currentTime = 0;
    }

    video
      .play()
      .then(() => {
        setPlayingIntroVideos((prev) => ({
          ...prev,
          [gameId]: true,
        }));
      })
      .catch((error) => {
        console.error("비디오 재생 실패:", error);
      });
  };

  // 비디오 영역 클릭 시 일시정지/재생 토글
  const handleVideoAreaClick = (event: React.MouseEvent, gameId: string) => {
    // 버튼 클릭은 무시
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === "BUTTON" || event.target.closest("button"))
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const video = introVideoRefs.current[gameId];
    if (!video) {
      return;
    }

    const isAtEnd =
      video.duration &&
      !isNaN(video.duration) &&
      video.currentTime >= video.duration - 0.1;

    if (video.paused) {
      if (isAtEnd) {
        video.currentTime = 0;
      }
      video
        .play()
        .then(() => {
          setPlayingIntroVideos((prev) => ({
            ...prev,
            [gameId]: true,
          }));
        })
        .catch((error) => {
          console.error("비디오 재생 실패:", error);
        });
    } else {
      video.pause();
      setPlayingIntroVideos((prev) => ({
        ...prev,
        [gameId]: false,
      }));
    }
  };

  const handleIntroVideoSkip = (event: React.MouseEvent, gameId: string) => {
    event.preventDefault();
    event.stopPropagation();
    const video = introVideoRefs.current[gameId];
    if (video) {
      if (video.duration && !isNaN(video.duration)) {
        video.currentTime = video.duration;
      }
      video.pause();
      setPlayingIntroVideos((prev) => ({
        ...prev,
        [gameId]: false,
      }));
    }
  };

  // 배속 변경 핸들러
  const handlePlaybackRateChange = (
    event: React.MouseEvent,
    gameId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const video = introVideoRefs.current[gameId];
    if (!video) return;

    const currentRate = playbackRates[gameId] || 1;
    const rates = [0.75, 1, 1.25, 1.5, 1.75, 2];
    const currentIndex = rates.indexOf(currentRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    const nextRate = rates[nextIndex];

    video.playbackRate = nextRate;
    setPlaybackRates((prev) => ({
      ...prev,
      [gameId]: nextRate,
    }));
  };

  // 모든 비디오 초기화 함수
  const resetAllVideos = () => {
    Object.values(introVideoRefs.current).forEach((video) => {
      if (video) {
        try {
          video.pause();
          video.currentTime = 0;
          video.playbackRate = 1;
        } catch (error) {
          console.error("비디오 초기화 실패:", error);
        }
      }
    });
    setPlayingIntroVideos({});
    setPlaybackRates({});
  };

  // 초기화면으로 돌아올 때 비디오 초기화
  useEffect(() => {
    if (selectedGameId === null) {
      resetAllVideos();
    }
  }, [selectedGameId]);

  const handleExit = () => {
    setSelectedGameId(null);
  };

  // 게임 실행
  if (selectedGameId) {
    return (
      <MissionExperience
        gameType={selectedGameId}
        participantLabel="참가자님"
        onExit={handleExit}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 px-10 py-7">
        <div className="w-full max-w-screen-hd space-y-10">
          <div className="text-center space-y-4">
            <span className="inline-flex items-center gap-3 rounded-full border border-cyan-400/50 bg-cyan-500/10 backdrop-blur-sm px-5 py-3 text-sm font-semibold uppercase tracking-[0.6em] text-cyan-300">
              <span className="h-2 w-2 rounded-full bg-cyan-400" />
              AI Career Mission
            </span>
            <h1 className="text-[3.5rem] font-bold text-white drop-shadow-[0_18px_45px_rgba(6,182,212,0.3)]">
              AI 공공서비스 직업체험
            </h1>
            <p className="text-2xl text-gray-300">
              설문 결과를 바탕으로 체험하고 싶은 직업 미션을 선택하세요.
              <br />각 미션은 5단계 시나리오로 구성되어 있으며, AI 활용 역량을
              직접 체감할 수 있습니다.
            </p>
          </div>

          <div className="grid gap-12 grid-cols-2 max-w-7xl mx-auto px-5">
            {GAME_OPTIONS.map((option) => (
              <div
                key={option.id}
                onClick={() => handleSelectGame(option.id)}
                className="group relative flex h-full flex-col gap-3 rounded-[1.25rem] bg-slate-800/60 backdrop-blur-sm hover:bg-slate-800/80 hover:shadow-[0_18px_50px_rgba(6,182,212,0.3)] border-4 border-cyan-500/50 hover:border-cyan-400 p-6 text-left transition-transform duration-300 hover:-translate-y-3 cursor-pointer"
              >
                <span className="flex items-center gap-4 text-[1.75rem] font-extrabold text-white">
                  <span>{option.heroEmoji}</span>
                  <LineBreakText
                    className="leading-snug"
                    value={option.heroTitle}
                  />
                </span>
                <p className="text-lg text-gray-300 leading-relaxed">
                  {option.description}
                </p>
                <div
                  className="relative mt-2 flex w-full items-center justify-center rounded-2xl border border-cyan-500/50 aspect-video cursor-pointer overflow-hidden"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVideoAreaClick(e, option.id);
                  }}
                >
                  {option.introVideo ? (
                    <>
                      <video
                        key={option.introVideo}
                        ref={(el) => {
                          introVideoRefs.current[option.id] = el;
                        }}
                        src={option.introVideo}
                        poster={option.introVideoPoster}
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover rounded-2xl"
                        onLoadedData={() =>
                          setIntroVideoReady((prev) => ({
                            ...prev,
                            [option.id]: true,
                          }))
                        }
                        onPlay={() => {
                          const video = introVideoRefs.current[option.id];
                          if (video) {
                            video.playbackRate = playbackRates[option.id] || 1;
                          }
                          setPlayingIntroVideos((prev) => ({
                            ...prev,
                            [option.id]: true,
                          }));
                        }}
                        onPause={() =>
                          setPlayingIntroVideos((prev) => ({
                            ...prev,
                            [option.id]: false,
                          }))
                        }
                        onEnded={() =>
                          setPlayingIntroVideos((prev) => ({
                            ...prev,
                            [option.id]: false,
                          }))
                        }
                      />
                      {!introVideoReady[option.id] && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-slate-900/80 backdrop-blur-sm ">
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-12 w-12 animate-spin text-cyan-400" />
                            <span className="text-lg font-semibold uppercase tracking-[0.35em] text-gray-300">
                              Loading...
                            </span>
                          </div>
                        </div>
                      )}
                      {introVideoReady[option.id] &&
                        !playingIntroVideos[option.id] && (
                          <button
                            type="button"
                            onClick={(event) =>
                              handleIntroVideoOverlayClick(event, option.id)
                            }
                            className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/35 opacity-0 transition-opacity duration-200 hover:opacity-100"
                          >
                            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-[#0EA5E9] shadow-lg">
                              <Play className="h-12 w-12" />
                            </span>
                          </button>
                        )}
                      {playingIntroVideos[option.id] && (
                        <>
                          <button
                            type="button"
                            onClick={(event) =>
                              handlePlaybackRateChange(event, option.id)
                            }
                            className="group absolute bottom-4 right-4 z-10 flex justify-center items-center gap-2 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-[#008FD3] hover:via-[#0096C0] hover:to-[#0EA5E9] px-5 py-2.5 text-base font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_12px_rgba(0,143,211,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-white/20 hover:border-white/40 overflow-hidden opacity-60 hover:opacity-100"
                          >
                            <Gauge className="h-5 w-5" />
                            <span>{playbackRates[option.id] || 1}x</span>
                          </button>
                          <button
                            type="button"
                            onClick={(event) =>
                              handleIntroVideoSkip(event, option.id)
                            }
                            className="group absolute top-4 right-4 z-10 flex justify-center items-center gap-2 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-[#008FD3] hover:via-[#0096C0] hover:to-[#0EA5E9] px-5 py-2.5 text-base font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_12px_rgba(0,143,211,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-white/20 hover:border-white/40 overflow-hidden opacity-60 hover:opacity-100"
                          >
                            <span>SKIP</span>
                            <SkipForward className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-black/70 text-lg font-semibold text-white/80 aspect-video">
                      인트로 영상이 준비 중입니다.
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">
                  <span className="inline-flex items-center gap-3 rounded-full bg-cyan-500/20 border border-cyan-400/30 px-4 py-2.5">
                    총 {option.scenarios.length} Stage
                  </span>
                  <span className="inline-flex items-center gap-3 rounded-full bg-cyan-500/20 border border-cyan-400/30 px-4 py-2.5">
                    AI 역량 집중 훈련
                  </span>
                </div>
                <span className="mt-auto inline-flex items-center justify-between px-4 py-2.5 text-base font-semibold text-white rounded-[1.25rem] bg-gradient-to-r from-cyan-600 via-cyan-500 to-cyan-600 hover:from-cyan-500 hover:via-cyan-400 hover:to-cyan-500 transition-transform duration-300 group-hover:scale-[1.02] shadow-lg shadow-cyan-500/20">
                  {option.selectionCta}
                  <ChevronRight className="h-5 w-5" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default GameMission;
